import React, { Component, ErrorInfo } from 'react';
import * as Sentry from "@sentry/react"; // Optional: For additional error tracking
import { AnalyticsManager, firestore } from './AnalyticsManager';
import { ErrorDetails } from './AnalyticsEvent';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';

interface Props {
    children: React.ReactNode;
    fallback?: React.ReactNode;
    onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
    hasError: boolean;
}

export class ErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError(_: Error): State {
        return { hasError: true };
    }

    async componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        const analytics = AnalyticsManager.getInstance();

        const errorDetails: ErrorDetails = {
            message: error.message,
            stack: error.stack,
            componentStack: errorInfo.componentStack ?? '',
            metadata: {
                timestamp: Date.now(),
                url: window.location.href,
                userAgent: navigator.userAgent
            }
        };

        try {
            // Track error in Firebase Analytics
            await analytics.track('error_boundary_caught', errorDetails);

            // Store error in Firestore
            await addDoc(collection(firestore, 'errors'), {
                ...errorDetails,
                timestamp: serverTimestamp()
            });

            // Optional: Send to Sentry for better error tracking
            Sentry.captureException(error, {
                extra: errorDetails
            });
        } catch (trackingError) {
            console.error('Failed to track error:', trackingError);
        }

        this.props.onError?.(error, errorInfo);
    }

    render() {
        if (this.state.hasError) {
            return this.props.fallback || (
                <div className="error-boundary-fallback">
                    <h2>Something went wrong</h2>
                    <button
                        onClick={() => window.location.reload()}
                        className="px-4 py-2 bg-blue-500 text-white rounded"
                    >
                        Refresh Page
                    </button>
                </div>
            );
        }

        return this.props.children;
    }
}